import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  CreateTopicRequest,
  Topic,
  TopicExamplesPage,
  UpdateTopicRequest,
} from '../types/TopicModels';
import {
  createTopic as createTopicApi,
  deleteTopic as deleteTopicApi,
  getTopicExamples,
  getTopics,
  updateTopic as updateTopicApi,
} from '../services/TopicService';
import { useApp } from './AppContext';

interface TopicState {
  topics: Topic[];
  isLoading: boolean;
  isSaving: boolean;
  error?: string;
  examplesPage?: TopicExamplesPage;
  isLoadingExamples: boolean;
}

interface TopicContextValue extends TopicState {
  fetchTopics: () => Promise<void>;
  createTopic: (req: CreateTopicRequest) => Promise<Topic>;
  updateTopic: (id: number, req: UpdateTopicRequest) => Promise<Topic>;
  deleteTopic: (id: number) => Promise<void>;
  fetchExamples: (categoryId?: number, page?: number) => Promise<void>;
  fetchNextExamplesPage: (categoryId?: number) => Promise<void>;
}

const TopicContext = createContext<TopicContextValue | undefined>(undefined);

export const TopicProvider = ({ children }: { children: ReactNode }) => {
  const { token } = useApp();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [examplesPage, setExamplesPage] = useState<TopicExamplesPage>();
  const [isLoadingExamples, setIsLoadingExamples] = useState(false);

  const fetchTopics = useCallback(async () => {
    if (!token) {
      return;
    }

    setIsLoading(true);
    setError(undefined);
    try {
      const response = await getTopics(token);
      setTopics(response.sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (err) {
      console.error('Error fetching topics:', err);
      setError('Unable to load topics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const createTopic = useCallback(
    async (req: CreateTopicRequest): Promise<Topic> => {
      if (!token) {
        throw new Error('Missing auth token');
      }

      setIsSaving(true);
      setError(undefined);
      try {
        const created = await createTopicApi(req, token);
        setTopics((prev) => [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder));
        return created;
      } catch (err) {
        console.error('Error creating topic:', err);
        setError('Unable to create topic. Please try again.');
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [token]
  );

  const updateTopic = useCallback(
    async (id: number, req: UpdateTopicRequest): Promise<Topic> => {
      if (!token) {
        throw new Error('Missing auth token');
      }

      setIsSaving(true);
      setError(undefined);
      try {
        const updated = await updateTopicApi(id, req, token);
        setTopics((prev) =>
          prev
            .map((topic) => (topic.id === id ? updated : topic))
            .sort((a, b) => a.sortOrder - b.sortOrder)
        );
        return updated;
      } catch (err) {
        console.error('Error updating topic:', err);
        setError('Unable to update topic. Please try again.');
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [token]
  );

  const deleteTopic = useCallback(
    async (id: number): Promise<void> => {
      if (!token) {
        throw new Error('Missing auth token');
      }

      setIsSaving(true);
      setError(undefined);
      try {
        await deleteTopicApi(id, token);
        setTopics((prev) => prev.filter((topic) => topic.id !== id));
      } catch (err) {
        console.error('Error deleting topic:', err);
        setError('Unable to delete topic. Please try again.');
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [token]
  );

  const fetchExamples = useCallback(
    async (categoryId?: number, page = 1) => {
      if (!token) {
        return;
      }

      setIsLoadingExamples(true);
      setError(undefined);
      try {
        const data = await getTopicExamples(token, categoryId, page);
        setExamplesPage(data);
      } catch (err) {
        console.error('Error fetching topic examples:', err);
        setError('Unable to load examples. Please try again.');
      } finally {
        setIsLoadingExamples(false);
      }
    },
    [token]
  );

  const fetchNextExamplesPage = useCallback(
    async (categoryId?: number) => {
      if (!token) {
        return;
      }

      const nextPage = (examplesPage?.page ?? 1) + 1;
      const pageSize = examplesPage?.pageSize ?? 10;
      const totalCount = examplesPage?.totalCount ?? 0;

      if (examplesPage && examplesPage.items.length >= totalCount) {
        return;
      }

      setIsLoadingExamples(true);
      setError(undefined);
      try {
        const next = await getTopicExamples(token, categoryId, nextPage, pageSize);
        setExamplesPage((prev) => {
          if (!prev) {
            return next;
          }

          const existingIds = new Set(prev.items.map((item) => item.id));
          const mergedItems = [
            ...prev.items,
            ...next.items.filter((item) => !existingIds.has(item.id)),
          ];

          return {
            ...next,
            items: mergedItems,
          };
        });
      } catch (err) {
        console.error('Error loading more examples:', err);
        setError('Unable to load more examples. Please try again.');
      } finally {
        setIsLoadingExamples(false);
      }
    },
    [examplesPage, token]
  );

  const value = useMemo<TopicContextValue>(
    () => ({
      topics,
      isLoading,
      isSaving,
      error,
      examplesPage,
      isLoadingExamples,
      fetchTopics,
      createTopic,
      updateTopic,
      deleteTopic,
      fetchExamples,
      fetchNextExamplesPage,
    }),
    [
      topics,
      isLoading,
      isSaving,
      error,
      examplesPage,
      isLoadingExamples,
      fetchTopics,
      createTopic,
      updateTopic,
      deleteTopic,
      fetchExamples,
      fetchNextExamplesPage,
    ]
  );

  return <TopicContext.Provider value={value}>{children}</TopicContext.Provider>;
};

export const useTopic = () => {
  const context = useContext(TopicContext);
  if (!context) {
    throw new Error('useTopic must be used within TopicProvider');
  }

  return context;
};
